import { formatDurationLabel } from "@clipboard-health/date-time";
import { Box, LinearProgress, Stack, type SxProps, type Theme } from "@mui/material";
import { CbhIcon, mergeSxProps, Text } from "@src/appV2/redesign/system/ui-components";
import pluralize from "pluralize";

import { ShiftCardWrapper } from "../../../Shift/Card/Wrapper";

interface LimitedBannerProps {
  score: number;
  showEmphasizedScore: boolean;
  thresholdScore: number;
  basketSize: number;
  qualificationDisplayLabelsLimited: string[];
  leadTimeInHours?: number;
  onHelpClick?: () => void;
  sx?: SxProps<Theme>;
}

export function LimitedBanner(props: LimitedBannerProps) {
  const {
    score,
    showEmphasizedScore,
    thresholdScore,
    basketSize,
    leadTimeInHours,
    qualificationDisplayLabelsLimited,
    onHelpClick,
    sx,
  } = props;

  const progressPercentage = Math.min((score / thresholdScore) * 100, 100);
  const unlockLabel = `Unlimited access at ${thresholdScore}pts`;

  if (score >= thresholdScore) {
    return null;
  }

  return (
    <ShiftCardWrapper
      sx={mergeSxProps(
        (theme) => ({
          paddingX: theme.spacing(5),
          paddingY: theme.spacing(4),
          background: theme.palette.onboarding?.inProgress.gradient,
          color: theme.palette.text.primary,
        }),
        sx
      )}
    >
      <Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text variant="body2">
            {/* For some reason, the b tag is not bold in the app in some pages. So we use this workaround for now. */}
            Status: <b style={{ fontWeight: "bold" }}>Limited</b>
          </Text>
          {onHelpClick && (
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Existing pattern, would require refactoring
            <Box role="button" sx={{ cursor: "pointer" }} onClick={onHelpClick}>
              <CbhIcon type="help-outlined" size="medium" />
            </Box>
          )}
        </Stack>

        {showEmphasizedScore && (
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={2}
            textAlign="center"
            justifyContent="center"
          >
            <Text variant="h1" sx={{ lineHeight: 1 }}>
              {score}
            </Text>
            <Text variant="h4">pts</Text>
          </Stack>
        )}

        <Box component="ul" sx={{ margin: 0, paddingLeft: 4 }}>
          <Box component="li" sx={{ typography: "body2", mb: 2 }}>
            {/* For some reason, the b tag is not bold in the app in some pages. So we use this workaround for now. */}
            <b style={{ fontWeight: "bold" }}>
              {basketSize} {pluralize("booking", basketSize)}
            </b>{" "}
            at a time.
          </Box>
          {leadTimeInHours && (
            <Box component="li" sx={{ typography: "body2", mb: 2 }}>
              Book up to&nbsp;
              {/* For some reason, the b tag is not bold in the app in some pages. So we use this workaround for now. */}
              <b style={{ fontWeight: "bold" }}>
                {formatDurationLabel(leadTimeInHours, "hour")}
              </b>{" "}
              ahead.
            </Box>
          )}
          {qualificationDisplayLabelsLimited.length > 0 && (
            <Box component="li" sx={{ typography: "body2", mb: 2 }}>
              Limit only applies to {qualificationDisplayLabelsLimited.join(", ")} shifts.
            </Box>
          )}
        </Box>

        <Stack spacing={1}>
          <Text variant="body2" textAlign="right">
            {unlockLabel}
          </Text>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={(theme) => ({
              height: 6,
              borderRadius: 999,
              backgroundColor: theme.palette.common.white,
              "& .MuiLinearProgress-bar": {
                backgroundColor: theme.palette.text.primary,
              },
            })}
          />
        </Stack>
      </Stack>
    </ShiftCardWrapper>
  );
}
